import React, { Component, CSSProperties, useCallback, useState, useMemo } from 'react';
import { IDocument } from 'types/backend_models/documents/IDocument';
import { GenericDataState } from 'redux/reducers/data_action_reducers/generic/GenericDataReducers';
import { ITableData } from 'types/backend_models/shared/ITableData';
import ElevatedContainer from 'components/content/shared/styling/ElevatedContainer';
import LayoutRow from 'components/content/shared/styling/LayoutRow';
import { SearchOutlined, AppstoreOutlined, BarsOutlined } from '@ant-design/icons';
import Divider from 'components/content/shared/styling/Divider';
import DocumentButton from 'components/content/shared/buttons/DocumentButton';
import { downloadDocument } from 'functions/DocumentDownload';
import { DocumentLineButton } from 'components/content/shared/buttons/DocumentLineButton';
import { SimpleIconButton } from 'components/content/shared/buttons/IconButtons';
import { Tooltip } from 'antd';
import { isNullOrEmpty } from 'functions/extensions/StringExtensions';
import NoDataMessageContainer from 'components/content/shared/other/NoDataMessageContainer';

interface IProps
{
	documentsState: GenericDataState<ITableData<IDocument[]>>
}

enum DocumentsDisplayMode
{
	grid,
	rows
}

export function LibraryDocumentsContainer(props: IProps)
{
	const [state, setState] = useState({
		documentDisplayMode: DocumentsDisplayMode.rows,
		searchFilter: ''
	});

	function onSearchChanged(value: string)
	{
		setState({ ...state, searchFilter: value });
	}

	// Filters documents locally
	const filteredDocuments: IDocument[] = useMemo(() =>
	{
		if (isNullOrEmpty(state.searchFilter)) return props.documentsState.data.results;

		const filtered: IDocument[] = [];
		props.documentsState.data.results.forEach((doc: IDocument) =>
		{
			if (doc.originalFileName.indexOf(state.searchFilter) !== -1) {
				filtered.push(doc);
			}
		});

		return filtered;
	}, [state.searchFilter, props.documentsState.data.results])

	function onDocumentDisplaySwtich(displayMode: DocumentsDisplayMode)
	{
		if (state.documentDisplayMode !== displayMode) {
			setState({ ...state, documentDisplayMode: displayMode });
		}
	}

	return (
		<div>
			{props.documentsState.data.results.length !== 0 &&
				<ElevatedContainer className="mgt-20">
					<LayoutRow>
						<div className="col-7 col-m-auto flex">
							<SearchOutlined className="fill-main-color ant-icon--center ant-icon--medium mgr-10" />
							<SearchBar
								className="col-auto"
								onSearchChanged={onSearchChanged}
							/>
						</div>
						<div className="col-3 col-m-1 txt-right">

							<SimpleIconButton
								onClick={() => onDocumentDisplaySwtich(DocumentsDisplayMode.grid)}
								active={state.documentDisplayMode === DocumentsDisplayMode.grid}
							>
								<Tooltip title="Grid layout">
									<AppstoreOutlined className="ant-icon--large ant-icon--center" />
								</Tooltip>
							</SimpleIconButton>

							<SimpleIconButton
								className="mgl-10"
								onClick={() => onDocumentDisplaySwtich(DocumentsDisplayMode.rows)}
								active={state.documentDisplayMode === DocumentsDisplayMode.rows}
							>
								<Tooltip title="Row layout">
									<BarsOutlined className="ant-icon--large ant-icon--center" />
								</Tooltip>
							</SimpleIconButton>
						</div>
					</LayoutRow>
					<Divider className="bg-border-color-light" />
					<DocumentsView
						documents={filteredDocuments}
						displayMode={state.documentDisplayMode}
					/>
				</ElevatedContainer>
			}
		</div>
	);
};



interface ISearchBarProps
{
	onSearchChanged: (search: string) => void,
	defaultValue?: string,
	className?: string,
	style?: CSSProperties
}

class SearchBar extends Component<ISearchBarProps>
{
	private _timeOut: NodeJS.Timeout | null = null;

	constructor(props: ISearchBarProps)
	{
		super(props);
		this.onValueChanged = this.onValueChanged.bind(this);
	}

	onValueChanged(event: React.ChangeEvent<HTMLInputElement>)
	{
		if (this._timeOut !== null) {
			clearTimeout(this._timeOut);
		}

		// Store target value here, because it event data will be nullified by React in timeout callback
		// Read: https://reactjs.org/docs/events.html#event-pooling
		const targetValue: string = event.target.value;
		this.props.onSearchChanged(targetValue);

		// this._timeOut = setTimeout(() =>
		// {
		// 	this.props.onSearchChanged(targetValue);
		// 	this._timeOut = null;
		// }, 0);
	}

	render()
	{
		const props = this.props;
		let className = "naked-input inline-flex flex-v-center txt-larger";
		if (props.className) className += " " + props.className;

		return (
			<input
				// style={{ fontSize: 20 }}
				style={props.style}
				className={className}
				placeholder="Search by document name"
				defaultValue={props.defaultValue}
				onChange={this.onValueChanged}
			/>
		);
	}
}

interface IDocumentsViewProps
{
	documents: IDocument[],
	displayMode: DocumentsDisplayMode,
	className?: string
}

function DocumentsView(props: IDocumentsViewProps) 
{
	const onClick = useCallback((doc: IDocument) =>
	{
		downloadDocument(doc);
	}, []);

	if (props.documents.length === 0) {
		return (
			<LayoutRow className={props.className + ' mgt-10'}>
				<NoDataMessageContainer containerClassName="mh-vh-50 w100p" message="Could not find any documents by that name." />
			</LayoutRow>
		)
	}

	if (props.displayMode === DocumentsDisplayMode.grid) {
		return (
			<LayoutRow className={props.className + ' mgt-10'}>
				{props.documents.map((doc: IDocument, index: number) =>
				{
					return (
						<div key={index} className="col-12 col-s-12 col-m-4 col-l-2 mgy-20">
							<DocumentButton document={doc} onClick={onClick} tooltip="Click to download" />
						</div>
					)
				})}
			</LayoutRow>
		);
	} else {
		return (
			<LayoutRow className={props.className + ' mgt-10'}>
				{props.documents.map((doc: IDocument, index: number) =>
				{
					let className = "col-12";
					if (index % 2 !== 0) className += " bg-grey";

					return (
						<div key={index} className={className}>
							<DocumentLineButton
								document={doc}
								onClick={onClick}
								// tooltip="Click to download"
							/>
						</div>
					)
				})}
			</LayoutRow>
		)
	}
}